"use client";

import type { MessageDescriptor } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import type { ToolUIPart } from "ai";
import { AlertTriangleIcon } from "lucide-react";
import {
  Component,
  type ComponentProps,
  type ComponentType,
  createContext,
  type ErrorInfo,
  type LazyExoticComponent,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import type { ToolApprovalReviewProps } from "./types";

type ToolApprovalActionLabels = {
  approveLabel: MessageDescriptor;
  denyLabel: MessageDescriptor;
};

/** Tool 固有 review が共通 approve action に要求できる表示状態。 */
export type ToolApprovalActionState = "enabled" | "disabled" | "hidden";

type ToolApprovalActionLabelContextValue = {
  approveActionState: ToolApprovalActionState;
  labels: ToolApprovalActionLabels;
  setRequestedApproveActionState: (state: ToolApprovalActionState) => void;
  setOverride: (labels: ToolApprovalActionLabels | null) => void;
  setReviewReady: (ready: boolean) => void;
};

const ToolApprovalActionLabelContext =
  createContext<ToolApprovalActionLabelContextValue | null>(null);

type ApprovalToolInputResult<T> =
  | { success: true; data: T }
  | { success: false; data?: never };

/**
 * native approval request の tool input を client 表示用に読み取ります。
 *
 * Tool ごとの server-only schema を client bundle に重複させず、転送途中で
 * 空または object 以外になっていないことだけを表示境界で確認します。
 */
export function readApprovalToolInput<T>(
  input: unknown,
  canDisplay: (input: T) => boolean = () => true,
): ApprovalToolInputResult<T> {
  if (
    typeof input !== "object" ||
    input === null ||
    Array.isArray(input) ||
    Object.keys(input).length === 0
  ) {
    return { success: false };
  }
  const data = input as T;
  return canDisplay(data) ? { success: true, data } : { success: false };
}

/**
 * Tool 固有レビューの内容だけを共通 approval surface に埋め込みます。
 *
 * 各 Tool が standalone 表示向けに持っていた外枠・背景・余白はここで打ち消し、
 * 共通 card の中に card が重なる見た目を避けます。
 */
export function ToolApprovalReviewContent({
  className,
  ...props
}: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "min-w-0 text-sm [&>:first-child]:!rounded-none [&>:first-child]:!border-0 [&>:first-child]:!bg-transparent [&>:first-child]:!p-0 [&>:first-child]:!shadow-none",
        className,
      )}
      {...props}
    />
  );
}

/** approval inputをTool固有schemaで解釈できない場合の共通レビュー案内です。 */
export function InvalidApprovalToolInputAlert() {
  return (
    <div
      className="flex gap-2 text-amber-800 text-xs dark:text-amber-200"
      role="alert"
    >
      <AlertTriangleIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0"
      />
      <Trans>
        Tool-specific details are unavailable because the request is invalid.
        Deny this request and ask the AI to create a new one.
      </Trans>
    </div>
  );
}

/** 現在resourceの補助情報だけを取得できない場合の共通レビュー案内です。 */
export function CurrentResourceDetailsUnavailableAlert() {
  return (
    <div
      className="flex gap-2 text-amber-800 text-xs dark:text-amber-200"
      role="alert"
    >
      <AlertTriangleIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0"
      />
      <Trans>
        Current resource details could not be loaded. Cancel this request and
        try again.
      </Trans>
    </div>
  );
}

/**
 * Tool 固有レビューが button の文言と表示状態だけを上書きできる表示境界です。
 *
 * approval ID・handler は公開せず、native approval の実行契約を共通 card の外へ
 * 漏らしません。
 */
export function ToolApprovalActionLabelProvider({
  approveLabel,
  denyLabel,
  reviewRequired = false,
  children,
}: ToolApprovalActionLabels & {
  reviewRequired?: boolean;
  children: ReactNode;
}) {
  const [override, setOverride] = useState<ToolApprovalActionLabels | null>(
    null,
  );
  const [requestedApproveActionState, setRequestedApproveActionState] =
    useState<ToolApprovalActionState>("enabled");
  const [reviewReady, setReviewReady] = useState(false);
  const approveActionState =
    reviewRequired && !reviewReady ? "disabled" : requestedApproveActionState;
  const defaultLabels = useMemo(
    () => ({ approveLabel, denyLabel }),
    [approveLabel, denyLabel],
  );
  const labels = override ?? defaultLabels;
  const value = useMemo(
    () => ({
      approveActionState,
      labels,
      setRequestedApproveActionState,
      setOverride,
      setReviewReady,
    }),
    [approveActionState, labels],
  );

  return (
    <ToolApprovalActionLabelContext.Provider value={value}>
      {children}
    </ToolApprovalActionLabelContext.Provider>
  );
}

/** 共通 approval card が現在の表示用 action label を読み取ります。 */
export function useToolApprovalActionLabels(): ToolApprovalActionLabels {
  const context = useContext(ToolApprovalActionLabelContext);
  if (!context) {
    throw new Error(
      "useToolApprovalActionLabels must be used within ToolApprovalActionLabelProvider",
    );
  }
  return context.labels;
}

/** 共通 approval card が承認 action の表示状態を読み取ります。 */
export function useToolApprovalActionState(): ToolApprovalActionState {
  const context = useContext(ToolApprovalActionLabelContext);
  if (!context) {
    throw new Error(
      "useToolApprovalActionState must be used within ToolApprovalActionLabelProvider",
    );
  }
  return context.approveActionState;
}

/**
 * Tool 固有レビューが query 状態に応じて button 文言だけを差し替えます。
 *
 * 同じ表示状態では module-scope など identity が安定した descriptor を渡してください。
 * component の破棄時には既定値へ戻し、別の Tool request へ表示状態を持ち越しません。
 */
export function useToolApprovalActionLabelOverride({
  approveLabel,
  denyLabel,
}: ToolApprovalActionLabels) {
  const context = useContext(ToolApprovalActionLabelContext);
  const setOverride = context?.setOverride;
  useEffect(() => {
    if (!setOverride) return;
    setOverride({ approveLabel, denyLabel });
    return () => setOverride(null);
  }, [approveLabel, denyLabel, setOverride]);
}

/**
 * Tool 固有レビューが照合状態に応じて approve action の表示状態を差し替えます。
 *
 * deny action と native approval handler は共通 card が保持します。
 */
export function useToolApprovalActionStateOverride(
  approveActionState: ToolApprovalActionState,
) {
  const context = useContext(ToolApprovalActionLabelContext);
  const setRequestedApproveActionState =
    context?.setRequestedApproveActionState;
  useLayoutEffect(() => {
    if (!setRequestedApproveActionState) return;
    setRequestedApproveActionState(approveActionState);
    return () => setRequestedApproveActionState("enabled");
  }, [approveActionState, setRequestedApproveActionState]);
}

/** lazy review subtree が正常に commit した期間だけ approval を解放します。 */
function ToolApprovalReviewReadyMarker() {
  const context = useContext(ToolApprovalActionLabelContext);
  const setReviewReady = context?.setReviewReady;

  useLayoutEffect(() => {
    if (!setReviewReady) return;
    setReviewReady(true);
    return () => setReviewReady(false);
  }, [setReviewReady]);

  return null;
}

type ToolApprovalReviewErrorBoundaryProps = {
  children: ReactNode;
  resetKey: string;
};

type ToolApprovalReviewErrorBoundaryState = {
  failed: boolean;
  resetKey: string;
};

class ToolApprovalReviewErrorBoundary extends Component<
  ToolApprovalReviewErrorBoundaryProps,
  ToolApprovalReviewErrorBoundaryState
> {
  state: ToolApprovalReviewErrorBoundaryState = {
    failed: false,
    resetKey: this.props.resetKey,
  };

  static getDerivedStateFromError(): Partial<ToolApprovalReviewErrorBoundaryState> {
    return { failed: true };
  }

  static getDerivedStateFromProps(
    props: ToolApprovalReviewErrorBoundaryProps,
    state: ToolApprovalReviewErrorBoundaryState,
  ): Partial<ToolApprovalReviewErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { failed: false, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(error, {
      component: "ToolApprovalReview",
      errorInfo,
      resetKey: this.props.resetKey,
    });
  }

  render() {
    if (this.state.failed) {
      return (
        <p className="text-muted-foreground text-xs" role="alert">
          <Trans>
            Tool-specific request details could not be loaded. Deny this request
            and ask the AI to create a new one.
          </Trans>
        </p>
      );
    }
    return this.props.children;
  }
}

/**
 * Tool 単位の lazy review を共通 fallback から分離して描画します。
 *
 * chunk load・render の失敗はこの境界内で止め、approval actions は親の
 * `NativeToolApprovalCard` に残します。
 */
export function ToolApprovalReview({
  component: Review,
  part,
  phase,
}: {
  component: LazyExoticComponent<ComponentType<ToolApprovalReviewProps>>;
  part: ToolUIPart;
  phase: ToolApprovalReviewProps["phase"];
}) {
  const resetKey = `${part.toolCallId}:${phase}`;
  return (
    <ToolApprovalReviewErrorBoundary resetKey={resetKey}>
      <Suspense
        fallback={
          // 複数の approval が同時に待機しても live region を重複させない。
          // 読み上げる進行状況は Activity が持つ app 共通の status へ集約する。
          <div className="flex items-center gap-2 py-1">
            <Skeleton className="h-3 w-24" />
            <span className="text-muted-foreground text-xs">
              <Trans>Loading confirmation...</Trans>
            </span>
          </div>
        }
      >
        <Review input={part.input} phase={phase} />
        <ToolApprovalReviewReadyMarker key={resetKey} />
      </Suspense>
    </ToolApprovalReviewErrorBoundary>
  );
}
